import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, ArrowClockwise, Coffee, X } from "@phosphor-icons/react";

const STORAGE_KEY = "global_pomodoro_state_v1";

type PomState = {
  mode: "focus" | "break";
  remaining: number; // seconds
  focusLength: number; // minutes
  breakLength: number; // minutes
  running: boolean;
};

const defaultState: PomState = {
  mode: "focus",
  remaining: 25 * 60,
  focusLength: 25,
  breakLength: 5,
  running: false,
};

function formatTime(s: number) {
  const m = Math.floor(s / 60)
    .toString()
    .padStart(2, "0");
  const sec = Math.floor(s % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${sec}`;
}

export default function GlobalPomodoro({
  visible = true,
  onClose,
}: {
  visible?: boolean;
  onClose?: () => void;
}) {
  const [state, setState] = useState<PomState>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as PomState) : defaultState;
    } catch {
      return defaultState;
    }
  });

  const tickRef = useRef<number | null>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    left: number;
    top: number;
  } | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  // load saved position
  useEffect(() => {
    try {
      const raw = localStorage.getItem("global_pomodoro_pos_v1");
      if (raw) {
        const p = JSON.parse(raw);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setPos({ left: p.left, top: p.top });
      } else if (typeof window !== "undefined") {
        // default bottom-right on desktop; on small screens keep pos null so we anchor by CSS
        if (window.innerWidth >= 640) {
          const defaultLeft = Math.max(12, window.innerWidth - 16 - 224);
          const defaultTop = Math.max(80, window.innerHeight - 120 - 20);
          setPos({ left: defaultLeft, top: defaultTop });
        } else {
          setPos(null);
        }
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!state.running) {
      if (tickRef.current) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return;
    }

    tickRef.current = window.setInterval(() => {
      setState((s) => {
        if (s.remaining <= 1) {
          // switch modes
          const nextMode = s.mode === "focus" ? "break" : "focus";
          const nextRemaining =
            (nextMode === "focus" ? s.focusLength : s.breakLength) * 60;
          // play sound
          try {
            const a = new Audio(
              "https://actions.google.com/sounds/v1/alarms/alarm_clock.ogg",
            );
            a.volume = 0.6;
            a.play().catch(() => {});
          } catch {}
          return {
            ...s,
            mode: nextMode,
            remaining: nextRemaining,
            running: false,
          };
        }
        return { ...s, remaining: s.remaining - 1 };
      });
    }, 1000);

    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current);
      tickRef.current = null;
    };
  }, [state.running, state.focusLength, state.breakLength]);

  useEffect(() => {
    if (pos)
      localStorage.setItem("global_pomodoro_pos_v1", JSON.stringify(pos));
  }, [pos]);

  const startPause = () => setState((s) => ({ ...s, running: !s.running }));
  const reset = () =>
    setState((s) => ({
      ...s,
      remaining: s.mode === "focus" ? s.focusLength * 60 : s.breakLength * 60,
      running: false,
    }));

  const changeLength = (which: "focus" | "break", delta: number) =>
    setState((s) => {
      const next = { ...s };
      if (which === "focus")
        next.focusLength = Math.max(1, Math.min(120, next.focusLength + delta));
      else
        next.breakLength = Math.max(1, Math.min(60, next.breakLength + delta));
      // if not running and current mode == which, update remaining
      if (!next.running && next.mode === which) {
        next.remaining =
          (which === "focus" ? next.focusLength : next.breakLength) * 60;
      }
      return next;
    });

  // Do not early-return when hidden; keep hooks stable — render hidden container instead
  const hidden = !visible;

  const startDrag = (e: React.PointerEvent) => {
    // disable dragging on small screens
    if (typeof window !== "undefined" && window.innerWidth < 640) return;
    // ignore if clicked a button inside the header
    const targetBtn = (e.target as HTMLElement).closest("button");
    if (targetBtn) return;
    if (!pos) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      left: pos.left,
      top: pos.top,
    };
    setDragging(true);

    const onMove = (ev: PointerEvent) => {
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      setPos({
        left: Math.max(
          8,
          Math.min(window.innerWidth - 200, dragRef.current.left + dx),
        ),
        top: Math.max(
          8,
          Math.min(window.innerHeight - 80, dragRef.current.top + dy),
        ),
      });
    };

    const onUp = (ev: PointerEvent) => {
      setDragging(false);
      dragRef.current = null;
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
    };

    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  };

  // ensure touch devices default to anchored layout
  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 640) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPos(null);
    }
    const onResize = () => {
      if (window.innerWidth < 640) setPos(null);
      else {
        // if pos is null and we moved to desktop, restore default
        setPos(
          (p) =>
            p ?? {
              left: Math.max(12, window.innerWidth - 16 - 224),
              top: Math.max(80, window.innerHeight - 120 - 20),
            },
        );
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // On small screens anchor bottom-right with responsive width
  const mobileAnchorProps = pos
    ? {}
    : { className: "pom-root fixed z-50 right-4 bottom-20", style: undefined };

  return (
    <div
      {...mobileAnchorProps}
      aria-hidden={hidden}
      style={
        hidden
          ? { display: "none" }
          : pos
            ? { position: "fixed", left: pos.left + "px", top: pos.top + "px" }
            : undefined
      }
    >
      <div className="w-56 sm:w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-lg p-3">
        <div
          className={`flex items-center justify-between mb-2 ${dragging ? "cursor-grabbing" : "cursor-grab"}`}
          onPointerDown={startDrag}
        >
          <div className="flex items-center gap-2">
            <Coffee className="text-amber-500" size={16} />
            <div>
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                Pomodoro
              </div>
              <div className="text-[11px] text-gray-400">
                {state.mode === "focus" ? "Focus" : "Break"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-sm font-mono font-bold text-gray-900 dark:text-white">
              {formatTime(state.remaining)}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose?.();
              }}
              className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-1">
            <button
              onClick={() => changeLength("focus", -5)}
              className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-xs"
            >
              -5
            </button>
            <div className="text-xs">{state.focusLength}m</div>
            <button
              onClick={() => changeLength("focus", 5)}
              className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-xs"
            >
              +5
            </button>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => changeLength("break", -1)}
              className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-xs"
            >
              -1
            </button>
            <div className="text-xs">{state.breakLength}m</div>
            <button
              onClick={() => changeLength("break", 1)}
              className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-xs"
            >
              +1
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={startPause}
              className="flex items-center gap-2 px-3 py-1 rounded-xl bg-primary text-white text-sm"
            >
              {state.running ? <Pause size={14} /> : <Play size={14} />}{" "}
              {state.running ? "Pause" : "Start"}
            </button>
            <button
              onClick={reset}
              className="flex items-center gap-2 px-2 py-1 rounded-xl bg-gray-100 dark:bg-gray-700 text-sm"
            >
              <ArrowClockwise size={14} />
            </button>
          </div>
          <div className="text-xs text-gray-400">Persistent</div>
        </div>
      </div>
    </div>
  );
}
